import { IContract, IUnreadTicketMessages } from '../models';
import { Milliseconds, dateStringToMilliseconds } from './dates';
import {
  IMasterdataChangeRequestResponseBean,
  masterDataChangeRequestErrorMessages,
} from '../models/masterdata/masterdata.model';
import { TranslateService } from '@ngx-translate/core';

// Contract status constants
const CUSTOM_CONTRACT_STATUS = {
  ACTIVE: 'active',
  NOT_STARTED: 'not_started',
  ENDED: 'ended',
} as const;

type CustomContractStatus = (typeof CUSTOM_CONTRACT_STATUS)[keyof typeof CUSTOM_CONTRACT_STATUS];

const mapContractDatesToTimestamps = (contracts: IContract[]): IContract[] => {
  if (!contracts?.length) {
    return [];
  }

  return contracts.map(contract => {
    const contractStartTime = contract.contractStart
      ? dateStringToMilliseconds(contract.contractStart)
      : null;
    const contractEndTime = contract.contractEnd
      ? dateStringToMilliseconds(contract.contractEnd)
      : null;
    return {
      ...contract,
      contractStartTime,
      contractEndTime,
    };
  });
};

const getCustomContractStatus = (
  contract: IContract,
  now: Milliseconds = Date.now()
): CustomContractStatus => {
  const { contractStartTime, contractEndTime } = contract;

  if (contractStartTime && contractStartTime > now) {
    return CUSTOM_CONTRACT_STATUS.NOT_STARTED;
  } else if (contractEndTime && contractEndTime < now) {
    return CUSTOM_CONTRACT_STATUS.ENDED;
  } else {
    return CUSTOM_CONTRACT_STATUS.ACTIVE;
  }
};

const getTypePriority = (type: string): number => {
  switch (type?.toUpperCase()) {
    case 'FLAT':
      return 1;
    case 'COMMERCIAL':
      return 2;
    case 'GARAGE':
      return 3;
    default:
      return 4;
  }
};

const getStatusPriority = (status: CustomContractStatus): number => {
  switch (status) {
    case CUSTOM_CONTRACT_STATUS.ACTIVE:
      return 1;
    case CUSTOM_CONTRACT_STATUS.NOT_STARTED:
      return 2;
    case CUSTOM_CONTRACT_STATUS.ENDED:
      return 3;
    default:
      return 4;
  }
};

export const sortContracts = (contracts: IContract[]): IContract[] => {
  if (!contracts?.length) {
    return [];
  }

  return [...mapContractDatesToTimestamps(contracts)].sort((a: IContract, b: IContract) => {
    const statusA = getCustomContractStatus(a);
    const statusB = getCustomContractStatus(b);

    const statusPriorityDiff = getStatusPriority(statusA) - getStatusPriority(statusB);
    if (statusPriorityDiff !== 0) {
      return statusPriorityDiff;
    }
    const typePriorityDiff = getTypePriority(a.type) - getTypePriority(b.type);
    if (typePriorityDiff !== 0) {
      return typePriorityDiff;
    }

    return a.contractStartTime && b.contractStartTime
      ? a.contractStartTime - b.contractStartTime
      : 0;
  });
};

export const getContractAddressText = (contract: IContract): string => {
  const street = contract.property?.address?.street || '';
  const houseNumber = contract.property?.address?.houseNumber || '';
  const externalId = contract.externalId || '';

  let result = street;

  if (houseNumber && street !== houseNumber) {
    result += ' ' + houseNumber;
  }

  if (externalId) {
    result += ' (' + externalId + ')';
  }

  return result;
};

export const getContractErrors = (
  response: IMasterdataChangeRequestResponseBean,
  contracts: IContract[],
  translateService?: TranslateService
): string[] => {
  // The response is null when the mutation itself failed (e.g. rejected by the
  // backend), so there are no per-contract statuses to report.
  return (response?.changeRequestStatuses ?? [])
    .filter(status => !status.success && status.errorMessage) // only failed ones with an error
    .map(status => {
      const contract = contracts.find(c => c.id === String(status.contractId));
      const name = contract ? getContractAddressText(contract) : `Contract ${status.contractId}`;
      const errorKey = masterDataChangeRequestErrorMessages[status.errorMessage]
        ? masterDataChangeRequestErrorMessages[status.errorMessage]
        : status.errorMessage;
      const errorMessage = translateService ? translateService.instant(errorKey) : errorKey;
      return `${name} ${errorMessage}`;
    });
};

// Contracts to mark for unread ticket messages: contractless ones (active or archived)
// concern every contract, otherwise only the listed ones.
export const getUnreadTicketContractIds = (
  contracts: Pick<IContract, 'id'>[],
  unread: IUnreadTicketMessages | null | undefined
): string[] => {
  if (unread?.contractless || unread?.archivedContractless) {
    return contracts.map(contract => contract.id);
  }
  return [...(unread?.contractIds ?? []), ...(unread?.archivedContractIds ?? [])];
};
